/**
 * Web shim for @react-native-community/datetimepicker.
 *
 * On web we emulate the Android imperative API with a temporary native
 * HTML input so existing React Native screens can keep using
 * DateTimePickerAndroid.open({...}) without screen-level changes.
 */

const DateTimePicker = () => null;

export default DateTimePicker;

const toDateInputValue = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return '';
  }

  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toTimeInputValue = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return '';
  }

  const hours = String(value.getHours()).padStart(2, '0');
  const minutes = String(value.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

const parseInputValue = (mode, value) => {
  if (!value) return null;

  if (mode === 'time') {
    const [hours = '0', minutes = '0'] = String(value).split(':');
    const nextDate = new Date();
    nextDate.setHours(Number(hours) || 0, Number(minutes) || 0, 0, 0);
    return nextDate;
  }

  const nextDate = new Date(value);
  return Number.isNaN(nextDate.getTime()) ? null : nextDate;
};

const emitDismissed = (onChange) => {
  onChange?.({ type: 'dismissed', nativeEvent: {} }, undefined);
};

const emitSelected = (onChange, nextDate) => {
  onChange?.({ type: 'set', nativeEvent: { timestamp: nextDate?.getTime?.() } }, nextDate);
};

const openPromptFallback = ({ value = new Date(), mode = 'date', onChange }) => {
  if (typeof window === 'undefined' || typeof window.prompt !== 'function') {
    emitDismissed(onChange);
    return Promise.resolve();
  }

  const initialValue = mode === 'time' ? toTimeInputValue(value) : toDateInputValue(value);
  const promptLabel =
    mode === 'time'
      ? 'Enter time in HH:mm format'
      : 'Enter date in YYYY-MM-DD format';

  const enteredValue = window.prompt(promptLabel, initialValue);
  if (enteredValue == null) {
    emitDismissed(onChange);
    return Promise.resolve();
  }

  const nextDate = parseInputValue(mode, enteredValue);
  if (!nextDate) {
    emitDismissed(onChange);
    return Promise.resolve();
  }

  emitSelected(onChange, nextDate);
  return Promise.resolve();
};

const openWebPicker = ({
  value = new Date(),
  mode = 'date',
  minimumDate,
  maximumDate,
  onChange,
}) => {
  if (typeof document === 'undefined') {
    return openPromptFallback({ value, mode, onChange });
  }

  const input = document.createElement('input');
  input.type = mode === 'time' ? 'time' : 'date';
  input.value = mode === 'time' ? toTimeInputValue(value) : toDateInputValue(value);

  if (minimumDate instanceof Date && !Number.isNaN(minimumDate.getTime())) {
    input.min = mode === 'time' ? toTimeInputValue(minimumDate) : toDateInputValue(minimumDate);
  }

  if (maximumDate instanceof Date && !Number.isNaN(maximumDate.getTime())) {
    input.max = mode === 'time' ? toTimeInputValue(maximumDate) : toDateInputValue(maximumDate);
  }

  input.style.position = 'fixed';
  input.style.left = '16px';
  input.style.top = '16px';
  input.style.width = '1px';
  input.style.height = '1px';
  input.style.opacity = '0.01';
  input.style.zIndex = '-1';

  const cleanup = () => {
    input.onchange = null;
    input.onblur = null;
    if (input.parentNode) {
      input.parentNode.removeChild(input);
    }
  };

  input.onchange = () => {
    const nextDate = parseInputValue(mode, input.value);
    emitSelected(onChange, nextDate);
    cleanup();
  };

  input.onblur = () => {
    cleanup();
  };

  document.body.appendChild(input);
  let opened = false;

  if (typeof input.showPicker === 'function') {
    try {
      input.showPicker();
      opened = true;
    } catch {
      // Some browsers require a stricter user gesture; click/focus above is the fallback.
    }
  }

  if (!opened) {
    try {
      input.focus();
      input.click();
      opened = true;
    } catch {
      // ignore and use prompt fallback below
    }
  }

  if (!opened) {
    cleanup();
    return openPromptFallback({ value, mode, onChange });
  }

  return Promise.resolve();
};

export const DateTimePickerAndroid = {
  open: openWebPicker,
  dismiss: () => {},
};

export const ACTION_DATE_SET = 'set';
export const ACTION_DISMISSED = 'dismissed';
export const ACTION_NEUTRAL = 'neutralButtonPressed';
export const ANDROID_DISPLAY = { DEFAULT: 'default', SPINNER: 'spinner', CALENDAR: 'calendar', CLOCK: 'clock' };
export const ANDROID_MODE = { DATE: 'date', TIME: 'time' };
export const EVENT_TYPE_SET = 'set';
export const EVENT_TYPE_DISMISSED = 'dismissed';
